import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useSignalR } from "../../hooks";
import videoService from "../../services/videoService";
import { CameraViewer } from "../CameraViewer/CameraViewer";
import { AudioTag } from "./components/AudioTag";
export const CameraView = ({ camera, droneId, disconnect, smallView, hidden = false, onStreamUpdate, muteMic, micVolume = 0.4, muteAudio, isViewOnly, }) => {
    const pcRef = useRef(null);
    const audioSenderRef = useRef(null);
    const rawMicStreamRef = useRef(null);
    const audioContextRef = useRef(null);
    const gainNodeRef = useRef(null);
    const micVolumeRef = useRef(micVolume);
    micVolumeRef.current = micVolume;
    const connectingRef = useRef(false);
    // Dedicated <audio> element for incoming drone speaker audio.
    // Always rendered (not conditional) so the ref is always valid.
    // display:none keeps it invisible; NOT muted so audio plays.
    const audioRef = useRef(null);
    const [micStream, setMicStream] = useState(null);
    const disconnectRef = useRef(disconnect);
    disconnectRef.current = disconnect;
    const droneIdRef = useRef(droneId);
    droneIdRef.current = droneId;
    const onStreamUpdateRef = useRef(onStreamUpdate);
    onStreamUpdateRef.current = onStreamUpdate;
    const micStreamRef = useRef(null);
    micStreamRef.current = micStream;
    const [stream, setStream] = useState(null);
    const [isCameraLoaded, setIsCameraLoaded] = useState(false);
    const [parkingSensors, setParkingSensors] = useState(null);
    const isAudio = camera.hasSpeaker;
    const isHasMic = camera.hasMicrophone;
    const connection = useSignalR();
    const ERR = (msg, ...args) => console.error(`[CameraView][${camera.name}] ${msg}`, ...args);
    // ─── Microphone init ──────────────────────────────────────────────────────
    useEffect(() => {
        //if (smallView) return;
        if (!isAudio || isViewOnly)
            return;
        const initMic = async () => {
            try {
                const ms = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    },
                });
                rawMicStreamRef.current = ms;
                const ctx = new AudioContext();
                audioContextRef.current = ctx;
                const gain = ctx.createGain();
                gain.gain.value = micVolumeRef.current;
                gainNodeRef.current = gain;
                const source = ctx.createMediaStreamSource(ms);
                const dest = ctx.createMediaStreamDestination();
                source.connect(gain);
                gain.connect(dest);
                setMicStream(dest.stream);
            }
            catch (err) {
                ERR("Microphone access denied:", err);
            }
        };
        initMic();
        return () => {
            var _a, _b;
            (_a = rawMicStreamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => t.stop());
            (_b = audioContextRef.current) === null || _b === void 0 ? void 0 : _b.close();
            rawMicStreamRef.current = null;
            audioContextRef.current = null;
            gainNodeRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAudio]);
    // ─── Mute / unmute mic ───────────────────────────────────────────────────
    useEffect(() => {
        var _a, _b, _c;
        //if (smallView) return;
        if (!isAudio || isViewOnly)
            return;
        if (muteMic) {
            (_a = rawMicStreamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => t.stop());
            (_b = audioContextRef.current) === null || _b === void 0 ? void 0 : _b.close();
            rawMicStreamRef.current = null;
            audioContextRef.current = null;
            gainNodeRef.current = null;
            (_c = audioSenderRef.current) === null || _c === void 0 ? void 0 : _c.replaceTrack(null).catch(() => { });
        }
        else {
            if (!audioSenderRef.current)
                return;
            navigator.mediaDevices
                .getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            })
                .then((ms) => {
                var _a;
                rawMicStreamRef.current = ms;
                const ctx = new AudioContext();
                audioContextRef.current = ctx;
                const gain = ctx.createGain();
                gain.gain.value = micVolumeRef.current;
                gainNodeRef.current = gain;
                const source = ctx.createMediaStreamSource(ms);
                const dest = ctx.createMediaStreamDestination();
                source.connect(gain);
                gain.connect(dest);
                const track = dest.stream.getAudioTracks()[0];
                if (track)
                    (_a = audioSenderRef.current) === null || _a === void 0 ? void 0 : _a.replaceTrack(track).catch(() => { });
            })
                .catch((err) => ERR("Mic re-access denied:", err));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [muteMic, isAudio]);
    // ─── Mic volume ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (gainNodeRef.current)
            gainNodeRef.current.gain.value = micVolume;
    }, [micVolume]);
    // ─── Parking sensors ─────────────────────────────────────────────────────
    useEffect(() => {
        if (hidden || !connection)
            return;
        const handler = (data) => {
            setParkingSensors({
                front: data[0],
                rear: data[2],
                left: data[4],
                right: data[1],
            });
        };
        connection.on("UpdateUltrasonicMeasurements", handler);
        return () => {
            connection.off("UpdateUltrasonicMeasurements", handler);
        };
    }, [hidden, connection]);
    // ─── Propagate stream state to parent ────────────────────────────────────
    useEffect(() => {
        var _a;
        /*LOG("Stream state changed — stream:", !!stream, "loaded:", isCameraLoaded);*/
        (_a = onStreamUpdateRef.current) === null || _a === void 0 ? void 0 : _a.call(onStreamUpdateRef, camera.name, stream, isCameraLoaded);
    }, [stream, isCameraLoaded, camera.name]);
    // ─── Main WebRTC setup ───────────────────────────────────────────────────
    useEffect(() => {
        if (!camera.name)
            return;
        if (disconnect)
            return;
        //if (isAudio && !micStream) return;
        const connectionId = uuidv4();
        connectingRef.current = false;
        const pc = new RTCPeerConnection();
        pcRef.current = pc;
        const startPlaying = async () => {
            if (!pcRef.current)
                return;
            let offer;
            try {
                offer = await videoService.getOffer(connectionId, camera.name, droneIdRef.current);
            }
            catch (err) {
                ERR("Error getting offer:", err);
                setTimeout(() => {
                    if (!disconnectRef.current && pcRef.current)
                        startPlaying();
                }, 1000);
                return;
            }
            if (!pcRef.current)
                return;
            try {
                await pc.setRemoteDescription(offer);
            }
            catch (err) {
                ERR("Error setting remote description:", err);
                return;
            }
            // ── Wire mic into the server's audio transceiver ──────────────────
            if (!smallView) {
                const transceivers = pc.getTransceivers();
                if (isAudio && micStreamRef.current) {
                    const micTrack = micStreamRef.current.getAudioTracks()[0];
                    if (!micTrack) {
                        ERR("No audio track in micStream");
                    }
                    else {
                        const audioTx = transceivers.find((t) => { var _a; return t.mid !== null && ((_a = t.receiver.track) === null || _a === void 0 ? void 0 : _a.kind) === "audio"; });
                        if (audioTx) {
                            audioTx.direction = "sendrecv";
                            audioSenderRef.current = audioTx.sender;
                            const liveTrack = micTrack.readyState === "live" ? micTrack : null;
                            if (liveTrack) {
                                liveTrack.stop();
                            }
                            audioTx.sender
                                .replaceTrack(liveTrack)
                                .catch((err) => ERR("replaceTrack() failed:", err));
                        }
                        else {
                            ERR("No matched audio transceiver:", transceivers.map((t) => {
                                var _a;
                                return ({
                                    mid: t.mid,
                                    kind: (_a = t.receiver.track) === null || _a === void 0 ? void 0 : _a.kind,
                                });
                            }));
                        }
                    }
                }
            }
            // Re-attach already-live tracks on reconnect
            pc.getReceivers().forEach((receiver) => {
                var _a;
                if (((_a = receiver.track) === null || _a === void 0 ? void 0 : _a.kind) === "video") {
                    setStream(new MediaStream([receiver.track]));
                    setIsCameraLoaded(true);
                }
            });
            let answer;
            try {
                answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
            }
            catch (err) {
                ERR("Error creating/setting answer:", err);
                return;
            }
            if (!pcRef.current || !pc.localDescription)
                return;
            pc.onconnectionstatechange = (event) => {
                const target = event.target;
                if (!disconnectRef.current &&
                    !connectingRef.current &&
                    target.connectionState === "disconnected") {
                    connectingRef.current = true;
                    pc.onconnectionstatechange = null;
                    startPlaying();
                }
            };
            try {
                await videoService.setAnswer(connectionId, droneIdRef.current, pc.localDescription);
                connectingRef.current = false;
            }
            catch (err) {
                ERR("Error sending answer:", err);
                setTimeout(() => {
                    if (!disconnectRef.current && pcRef.current)
                        startPlaying();
                }, 1000);
            }
        };
        // ── Event handlers ──────────────────────────────────────────────────
        pc.ontrack = ({ track, streams }) => {
            var _a;
            if (track.kind === "audio") {
                if (audioRef.current) {
                    audioRef.current.srcObject = new MediaStream([track]);
                    audioRef.current.muted = !!muteAudio;
                    audioRef.current
                        .play()
                        .catch((err) => ERR("Audio play() failed — likely autoplay policy:", err));
                }
                else {
                    ERR("audioRef.current is null — <audio> element not in DOM yet");
                }
            }
            else if (track.kind === "video") {
                const attachStream = (_a = streams[0]) !== null && _a !== void 0 ? _a : new MediaStream([track]);
                track.onunmute = () => {
                    setStream(attachStream);
                    setIsCameraLoaded(true);
                };
                if (track.readyState === "live") {
                    setStream(attachStream);
                    setIsCameraLoaded(true);
                }
            }
        };
        pc.onicecandidate = async (event) => {
            if (!event.candidate)
                return;
            try {
                await videoService.setIceCandidate(connectionId, droneIdRef.current, event.candidate);
            }
            catch (err) {
                ERR("Error sending ICE candidate:", err);
                setTimeout(() => {
                    if (!disconnectRef.current && pcRef.current)
                        startPlaying();
                }, 1000);
            }
        };
        setTimeout(startPlaying, 500);
        return () => {
            pc.onconnectionstatechange = null;
            pc.oniceconnectionstatechange = null;
            pc.onicecandidate = null;
            pc.ontrack = null;
            pc.onsignalingstatechange = null;
            pc.onicegatheringstatechange = null;
            pc.close();
            pcRef.current = null;
            audioSenderRef.current = null;
            setStream(null);
            setIsCameraLoaded(false);
            if (audioRef.current) {
                audioRef.current.srcObject = null;
            }
        };
    }, [camera.name, disconnect, isAudio, micStream]);
    return (_jsxs(_Fragment, { children: [isHasMic && !smallView && !isViewOnly && (_jsx(AudioTag, { audioRef: audioRef, muteAudio: !!muteAudio })), !hidden && (_jsx(CameraViewer, { camera: camera, stream: stream, isCameraLoaded: isCameraLoaded, parkingSensors: parkingSensors, smallView: smallView }))] }));
};
